import { EmbedContextOptions } from '@thoughtspot/blink-context';
import { ChartConfigParameters, VisualConfig } from '@thoughtspot/ts-chart-sdk';
import _ from 'lodash';
import {
    addActionItemMapFromUrlParams,
    shouldHideForEmbed,
    shouldShowAction,
} from '../embed.util';

export const getDisabledActionsCustomChart = (
    customChartVisualConfig: VisualConfig,
) => {
    if (customChartVisualConfig?.customChartDisabledActions) {
        return addActionItemMapFromUrlParams(
            new Set<string>(
                customChartVisualConfig?.customChartDisabledActions,
            ),
        );
    }
    return new Set<string>();
};
export const getHiddenActionsCustomChart = (
    chartConfigParameters: ChartConfigParameters,
) => {
    if (
        chartConfigParameters?.customChartVisualConfig?.customChartHiddenActions
    ) {
        return addActionItemMapFromUrlParams(
            new Set<string>(
                chartConfigParameters?.customChartVisualConfig?.customChartHiddenActions,
            ),
        );
    }
    return new Set<string>();
};
export const getVisibleActionsCustomChart = (
    chartConfigParameters: ChartConfigParameters,
) => {
    if (
        chartConfigParameters?.customChartVisualConfig
            ?.customChartVisibleActions
    ) {
        const visibleActionsIds = addActionItemMapFromUrlParams(
            new Set<string>(
                chartConfigParameters?.customChartVisualConfig?.customChartVisibleActions,
            ),
        );
        return visibleActionsIds;
    }
    return undefined;
};

export const shouldShowActionForCustomChart = (
    customChartVisualConfig: VisualConfig,
    actionId: string,
) => {
    if (_.isEmpty(customChartVisualConfig)) {
        return true;
    }
    if (!_.isEmpty(customChartVisualConfig?.customChartVisibleActions)) {
        const visibleActionIds = new Set<string>([
            ...customChartVisualConfig?.customChartVisibleActions,
        ]);
        if (visibleActionIds) {
            return visibleActionIds?.has(actionId);
        }
    }

    if (!_.isEmpty(customChartVisualConfig?.customChartHiddenActions)) {
        const hiddenActionIds = new Set<string>([
            ...customChartVisualConfig?.customChartHiddenActions,
        ]);

        if (hiddenActionIds?.has(actionId)) {
            return false;
        }
    }

    return true;
};
export const getFilterContentCustomChartSdk = (
    menuItems: any,
    chartConfigParameters: ChartConfigParameters,
) => {
    const customChartDisabledActions = getDisabledActionsCustomChart(
        chartConfigParameters.customChartVisualConfig,
    );
    const customChartDisabledActionReason =
        chartConfigParameters?.customChartVisualConfig
            ?.customChartDisabledActionReason || '';
    const customChartHiddenActions = getHiddenActionsCustomChart(
        chartConfigParameters,
    );

    const customChartVisibleActions = getVisibleActionsCustomChart(
        chartConfigParameters,
    );
    let filteredMenuItems: string[] = [];
    if (customChartVisibleActions) {
        filteredMenuItems = Object.keys(menuItems).filter(key =>
            customChartVisibleActions.has(key),
        );
        if (customChartHiddenActions) {
            filteredMenuItems = filteredMenuItems.filter(
                key => !customChartHiddenActions.has(key),
            );
        }
    }
    if (customChartHiddenActions && !customChartVisibleActions) {
        filteredMenuItems = Object.keys(menuItems).filter(
            key => !customChartHiddenActions.has(key),
        );
    }
    // add filter for BYOC actions passed via ts chart sdk.
    return filteredMenuItems.reduce((_menuItems, key) => {
        return {
            ..._menuItems,
            [key]: {
                ...menuItems[key],
                itemDisabled:
                    menuItems[key].itemDisabled ||
                    customChartDisabledActions?.has(key),
                itemDisabledTooltip: customChartDisabledActions?.has(key)
                    ? customChartDisabledActionReason
                    : menuItems[key].itemDisabledTooltip,
            },
        };
    }, {});
};

export const shouldShowActionMenuItems = (
    customChartVisualConfig: VisualConfig,
    actionId: string,
    visibleActionIds: Set<string>,
    hiddenActionIds: Set<string>,
    isPinboardAction = false,
) => {
    return (
        shouldShowActionForCustomChart(customChartVisualConfig, actionId) &&
        shouldShowAction(
            visibleActionIds,
            hiddenActionIds,
            actionId,
            isPinboardAction,
        )
    );
};

export const shouldShowMappedCustomAction = (
    embedConfig: EmbedContextOptions,
    action: string,
    customChartVisualConfig: VisualConfig,
) => {
    return (
        !shouldHideForEmbed(embedConfig, action) &&
        shouldShowActionForCustomChart(customChartVisualConfig, action)
    );
};

export const getDisabledActionReasonForMenuItem = (
    embedDisabledActions: Set<string>,
    embedDisabledReason: string,
    customChartDisabledActions: Set<string>,
    customChartDisabledActionReason: string,
    action: string,
    defaultTooltip: string,
) => {
    let disabledTooltip = embedDisabledActions.has(action)
        ? embedDisabledReason
        : defaultTooltip;
    disabledTooltip = customChartDisabledActions?.has(action)
        ? customChartDisabledActionReason
        : disabledTooltip;
    return disabledTooltip;
};
